import React from "react";
import { Instagram, Linkedin, Facebook, Mail } from "lucide-react";
import { useSiteContent, useLang } from "@/context/SiteContentContext";

export default function Footer() {
  const content = useSiteContent();
  const { lang } = useLang();
  const f = content?.footer || {};
  const c = content?.contact || {};
  const nav = content?.nav || {};
  const services = content?.services?.items || [];

  const labels = lang === "zh"
    ? { nav: "导航", services: "服务", contact: "联系" }
    : { nav: "Navigate", services: "Services", contact: "Contact" };

  const socials = [
    { icon: Instagram, href: f.instagram, key: "instagram" },
    { icon: Linkedin, href: f.linkedin, key: "linkedin" },
    { icon: Facebook, href: f.facebook, key: "facebook" },
    { icon: Mail, href: c.email ? `mailto:${c.email}` : "", key: "email" },
  ].filter((s) => s.href);

  return (
    <footer data-testid="site-footer" className="bg-slate-950 text-slate-300 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-16 border-b border-white/10">
          <div className="lg:col-span-5">
            <div className="font-serif-sc text-3xl text-white font-bold">
              Charles Lee<span className="text-orange-500">.</span>
            </div>
            <p className="mt-4 text-sm text-slate-400 max-w-md leading-relaxed">{f.tagline}</p>
            <div className="mt-6 flex items-center gap-3">
              {socials.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.key}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${s.key}`}
                    className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-orange-500 hover:text-orange-500 transition-colors"
                    aria-label={s.key}
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs tracking-[0.25em] uppercase text-slate-500 font-display mb-4">{labels.nav}</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="#about" className="hover:text-orange-500">{nav.about}</a></li>
              <li><a href="#isigroup" className="hover:text-orange-500">{nav.isigroup}</a></li>
              <li><a href="#services" className="hover:text-orange-500">{nav.services}</a></li>
              <li><a href="#testimonials" className="hover:text-orange-500">{nav.testimonials}</a></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs tracking-[0.25em] uppercase text-slate-500 font-display mb-4">{labels.services}</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              {services.slice(0, 4).map((s, i) => (
                <li key={i}>{s.title}</li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs tracking-[0.25em] uppercase text-slate-500 font-display mb-4">{labels.contact}</h4>
            <ul className="space-y-3 text-sm">
              {c.email && (
                <li><a href={`mailto:${c.email}`} className="hover:text-orange-500">{c.email}</a></li>
              )}
              {c.phone && <li className="text-slate-400">{c.phone}</li>}
              {c.address && <li className="text-slate-400">{c.address}</li>}
            </ul>
          </div>
        </div>

        <div className="pt-12 pb-6 overflow-hidden">
          <div className="font-display font-black tracking-tighter text-white/5 text-[18vw] lg:text-[15vw] leading-none select-none">
            ISI · CHARLES
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-6 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} iSi Group. All rights reserved.</div>
          <div className="font-display tracking-[0.2em] uppercase">Built with intention · Malaysia</div>
        </div>
      </div>
    </footer>
  );
}
